import React, { useState } from 'react';
import CommonHeader from '../../util_components/common_header';
import CustomSelect from '../../util_components/custom_single_select';
import CustomInput from '../../util_components/custom_input';
import Button from '../../util_components/button';
import { post_api } from '../../../redux/api_funcs';

const CURRENCY_OPTIONS = [
  { value: 'US', label: 'United States (USD)' },
  { value: 'CA', label: 'Canada (CAD)' },
  { value: 'AU', label: 'Australia (AUD)' },
  { value: 'GB', label: 'United Kingdom (GBP)' },
  { value: 'AT', label: 'Eurozone (EUR)' },
  { value: 'AE', label: 'United Arab Emirates (AED)' },
  { value: 'MY', label: 'Malaysia (MYR)' },
  { value: 'SA', label: 'Saudi Arabia (SAR)' },
  { value: 'ZA', label: 'South Africa (ZAR)' },
  { value: 'SG', label: 'Singapore (SGD)' },
  { value: 'NZ', label: 'New Zealand (NZD)' }
];

const SESSIONS_PER_WEEK_OPTIONS = [
  { value: 0, label: '0 Sessions/Week' },
  { value: 1, label: '1 Session/Week' },
  { value: 2, label: '2 Sessions/Week' },
  { value: 3, label: '3 Sessions/Week' },
  { value: 4, label: '4 Sessions/Week' },
  { value: 5, label: '5 Sessions/Week' },
  { value: 6, label: '6 Sessions/Week' },
  { value: 7, label: '7 Sessions/Week' }
];

const STUDENT_BASE =
  import.meta.env.VITE_APP_STAGE === 'production'
    ? 'https://myyogateacher.com'
    : 'https://student-testenv1.myyogateacher.com';

function PricingLinks() {
  const [currency, setCurrency] = useState('');
  const [sessionsPerWeek, setSessionsPerWeek] = useState('');
  const [genericIsHighVolume, setGenericIsHighVolume] = useState(false);
  const [magicIsHighVolume, setMagicIsHighVolume] = useState(false);
  const [email, setEmail] = useState('');
  const [genericLink, setGenericLink] = useState('');
  const [magicLink, setMagicLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkExpiry, setMagicLinkExpiry] = useState('');
  const [copySuccess, setCopySuccess] = useState({ generic: false, magic: false });

  const handleGenerateGenericLink = () => {
    if (currency === '') {
      alert('Please select a currency');
      return;
    }
    if (!sessionsPerWeek && !genericIsHighVolume) {
      alert('Please select sessions per week or enable Consistency Pricing');
      return;
    }
    const params = { country_code: currency };
    if (sessionsPerWeek !== '') params.s_per_week = sessionsPerWeek;
    if (genericIsHighVolume) params.pricing = 'consistency_plans';
    setGenericLink(`${STUDENT_BASE}/pricing?${new URLSearchParams(params).toString()}`);
    setMagicLink('');
    setMagicLinkExpiry('');
  };

  const handleGenerateMagicLink = async () => {
    if (!email) {
      alert('Please enter an email address');
      return;
    }
    if (!sessionsPerWeek && !magicIsHighVolume) {
      alert('Please select sessions per week or enable Consistency Pricing');
      return;
    }

    setLoading(true);
    try {
      const params = {};
      if (sessionsPerWeek !== '') params.s_per_week = sessionsPerWeek;
      if (magicIsHighVolume) params.pricing = 'consistency_plans';
      const payload = {
        email: email.trim(),
        redirect: `/pricing?${new URLSearchParams(params).toString()}`,
        is_short_link: true,
        allow_reattempt: 1,
        expiry_in_min: 1440
      };

      const url =
        import.meta.env.VITE_APP_STAGE !== 'production'
          ? 'https://gstg1.myyogateacher.com/v2/magic_link/get_magic_link_with_expiry'
          : 'https://gapi.myyogateacher.com/v2/magic_link/get_magic_link_with_expiry';
      const response = await post_api(url, payload);

      if (response.status === 201) {
        setMagicLink(response.data.data.MagicLink);
        setMagicLinkExpiry(response.data.data.MagicLinkExpiry);
        setGenericLink('');
      } else {
        alert('Failed to generate magic link. Please try again.');
      }
    } catch (error) {
      console.error('Error generating magic link:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async (type, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopySuccess((prev) => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [type]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      alert('Failed to copy to clipboard');
    }
  };

  const renderHighVolumeOption = (id, checked, onChange) => (
    <div className="mb-6 flex items-center gap-2">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 cursor-pointer"
      />
      <label htmlFor={id} className="text-sm font-medium cursor-pointer">
        Consistency Pricing
      </label>
    </div>
  );

  const renderLinkOutput = (link, type) => {
    if (!link) return null;

    return (
      <div className="mt-4">
        <div className="p-4 bg-gray-50 rounded-md">
          <p className="text-sm font-medium mb-2">Generated Link:</p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={link}
              readOnly
              className="flex-1 p-2 border rounded bg-white"
              onClick={(e) => e.target.select()}
            />
            <Button
              onClick={() => handleCopyLink(type, link)}
              custom_style={{
                backgroundColor: copySuccess[type] ? '#4CAF50' : undefined,
                minWidth: '100px'
              }}
            >
              {copySuccess[type] ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          {type === 'magic' && magicLinkExpiry && (
            <p className="text-sm text-gray-600 mt-2">Magic Link Expiry: {magicLinkExpiry}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4">
      <CommonHeader title="Pricing Links" />

      <div className="mt-8 max-w-2xl mx-auto">
        {/* Generic Link Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Generate Generic Link</h2>
          <div className="mb-6">
            <CustomSelect
              label="Currency"
              options={CURRENCY_OPTIONS}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="mb-6">
            <CustomSelect
              label="Sessions Per Week"
              options={SESSIONS_PER_WEEK_OPTIONS}
              value={sessionsPerWeek}
              onChange={(e) => setSessionsPerWeek(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          {renderHighVolumeOption('hv-generic', genericIsHighVolume, setGenericIsHighVolume)}
          <div className="flex justify-center">
            <Button
              onClick={handleGenerateGenericLink}
              disabled={currency === '' || (!sessionsPerWeek && !genericIsHighVolume)}
            >
              Generate Generic Link
            </Button>
          </div>

          {renderLinkOutput(genericLink, 'generic')}
        </div>

        {/* Magic Link Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Generate Magic Link</h2>
          <div className="mb-6">
            <CustomSelect
              label="Sessions Per Week"
              options={SESSIONS_PER_WEEK_OPTIONS}
              value={sessionsPerWeek}
              onChange={(e) => setSessionsPerWeek(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          {renderHighVolumeOption('hv-magic', magicIsHighVolume, setMagicIsHighVolume)}
          <div className="mb-6">
            <CustomInput
              label="Email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleGenerateMagicLink}
              disabled={!email || (!sessionsPerWeek && !magicIsHighVolume) || loading}
            >
              Generate Magic Link
            </Button>
          </div>

          {renderLinkOutput(magicLink, 'magic')}
        </div>
      </div>
    </div>
  );
}

export default PricingLinks;
